'use client';

import React from 'react';
import styles from './UploadContestGraphicForm.module.scss';
import { DynamicForm } from '@/components/forms/dynamic-form/DynamicForm';
import { createSchema, IAPIFormSchema } from '@/services/formSchema.service';
import { useTranslation } from '@app/i18n/useTranslationClient';
import { FieldValues, UseFormReset, UseFormSetError } from 'react-hook-form';
import { handleFormServerErrors } from '@/components/dynamic-field/field-input/fieldInput.helper';
import { Modal } from '@/components/modal/Modal';
import { useUploadContest } from '@/hooks/api/useUploadContest/useUploadContest';
import {
    IUploadContestRequestBody,
    IUploadContestResponse,
} from '@/hooks/api/useUploadContest/useUploadContest.interfaces';
import { UploadProjectFormSuccess } from '@/components/upload-project-form/upload-project-form-success/UploadProjectFormSuccess';

export interface IUploadContestGraphicForm {
    slug: string;
    uploadContestGraphicSchema?: IUploadContestResponse;
}

export const UploadContestGraphicForm: React.FC<IUploadContestGraphicForm> = ({ uploadContestGraphicSchema, slug }) => {
    const [isFormSubmitted, setSubmitForm] = React.useState(false);

    const {
        t,
        i18n: { language },
    } = useTranslation();

    const { uploadContest, loading } = useUploadContest();
    const schema = createSchema(uploadContestGraphicSchema as unknown as IAPIFormSchema, language, t);

    const onFormSubmit = async (data: FieldValues, setErrors: UseFormSetError<any>, reset: UseFormReset<any>) => {
        const response = await uploadContest({ slug }, {
            ...data,
            image: data.image[0],
        } as IUploadContestRequestBody);

        if (response.errors) {
            handleFormServerErrors(setErrors, response.errors);
        } else {
            reset();
            setSubmitForm(true);
        }
    };

    const handleCloseModal = () => {
        setSubmitForm(false);
    };

    if (isFormSubmitted) {
        return (
            <Modal isClosable={true} classes={styles.modal} onClose={handleCloseModal}>
                <UploadProjectFormSuccess
                    modalTitle={t('translations.contestProjectUpload.success.title')}
                    modalContent={t('translations.contestProjectUpload.success.content')}
                    setFormSent={setSubmitForm}
                    closeModal={handleCloseModal}
                    showNextProjectButton={false}
                />
            </Modal>
        );
    }

    return (
        <DynamicForm
            formSchema={schema}
            formSubmitButtonLabel={t('translations.forms.send')}
            isSubmitButtonDisabled={loading}
            onFormSubmit={onFormSubmit}
        />
    );
};
